import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import {
  Container,
  Typography,
  Button,
  Box,
  Stack,
} from '@mui/material';
import { Rocket, AutoAwesome } from '@mui/icons-material';

function CallToAction() {
  const [mounted, setMounted] = useState(false);
  const [ref, inView] = useInView({
    threshold: 0.3,
    triggerOnce: true,
  });

  useEffect(() => {
    setMounted(true);
  }, []);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.8,
        staggerChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut",
      },
    },
  };

  if (!mounted) return null;

  return (
    <Box
      id="cta"
      sx={{
        background: 'linear-gradient(135deg, var(--color-brand-primary), var(--color-brand-dark))',
        position: 'relative',
        overflow: 'hidden',
        py: 12,
      }}
    >
      {/* Animated background elements */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background:
            'radial-gradient(circle at 10% 20%, rgba(255, 255, 255, 0.1) 0%, transparent 40%), ' +
            'radial-gradient(circle at 90% 80%, rgba(255, 224, 92, 0.2) 0%, transparent 40%)',
          pointerEvents: 'none',
        }}
      />

      {/* Floating decoration */}
      <motion.div
        animate={{
          y: [0, -20, 0],
          rotate: [0, 5, 0],
        }}
        transition={{
          duration: 4,
          repeat: Infinity,
          ease: "easeInOut",
        }}
        style={{
          position: 'absolute',
          top: '10%',
          right: '10%',
        }}
      >
        <AutoAwesome sx={{ fontSize: 60, color: 'rgba(255, 255, 255, 0.2)' }} />
      </motion.div>

      <motion.div
        animate={{
          y: [0, 15, 0],
          rotate: [0, -5, 0],
        }}
        transition={{
          duration: 5,
          repeat: Infinity,
          ease: "easeInOut",
          delay: 1,
        }}
        style={{
          position: 'absolute',
          bottom: '15%',
          left: '8%',
        }}
      >
        <AutoAwesome sx={{ fontSize: 40, color: 'rgba(255, 224, 92, 0.3)' }} />
      </motion.div>

      <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1 }}>
        <motion.div
          ref={ref}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
          variants={containerVariants}
        >
          <Stack spacing={5} alignItems="center" textAlign="center">
            <motion.div variants={itemVariants}>
              <Typography
                variant="h2"
                sx={{
                  color: 'white',
                  fontWeight: 800,
                  mb: 2,
                  fontSize: { xs: '2rem', md: '3rem' },
                  textShadow: '0 4px 20px rgba(0, 0, 0, 0.2)',
                }}
              >
                Ready to Discover Your Career Story?
              </Typography>
            </motion.div>

            <motion.div variants={itemVariants}>
              <Typography
                variant="body1"
                sx={{
                  color: 'rgba(255, 255, 255, 0.9)',
                  fontSize: '1.25rem',
                  lineHeight: 1.7,
                  maxWidth: '600px',
                }}
              >
                Transform how you understand and communicate your unique professional value. Start for free—no credit card required.
              </Typography>
            </motion.div>

            <motion.div variants={itemVariants}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3}>
                <Button
                  component={motion.a}
                  href="https://app.kaziai.me"
                  whileHover={{
                    scale: 1.05,
                    boxShadow: '0 20px 40px rgba(0, 0, 0, 0.3)',
                  }}
                  whileTap={{ scale: 0.95 }}
                  variant="contained"
                  size="large"
                  sx={{
                    background: 'white',
                    color: 'var(--color-brand-primary)',
                    py: 2,
                    px: 5,
                    fontSize: '1.1rem',
                    fontWeight: 700,
                    borderRadius: '16px',
                    boxShadow: '0 10px 30px rgba(0, 0, 0, 0.2)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      background: 'var(--color-accent-primary)',
                      color: 'var(--color-brand-dark)',
                    },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    Start Free Today
                    <Rocket sx={{ fontSize: 22 }} />
                  </Box>
                </Button>
              </Stack>
            </motion.div>

            <motion.div variants={itemVariants}>
              <Stack direction="row" spacing={4} sx={{ mt: 2 }}>
                {['✓ Free forever plan', '✓ No credit card required'].map((item, index) => (
                  <Typography
                    key={index}
                    variant="body2"
                    sx={{
                      color: 'rgba(255, 255, 255, 0.8)',
                      fontWeight: 500,
                      display: { xs: index === 0 ? 'block' : 'none', sm: 'block' },
                    }}
                  >
                    {item}
                  </Typography>
                ))}
              </Stack>
            </motion.div>
          </Stack>
        </motion.div>
      </Container>
    </Box>
  );
}

export default CallToAction;
